import {LegacyWBIcon} from '@wandb/weave/common/components/elements/LegacyWBIcon';
import {Link, LinkNoCrawl} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import produce from 'immer';
import * as _ from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {SortFunction} from 'react-table';
import TimeAgo from 'react-timeago';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Popup} from 'semantic-ui-react';

import {SectionTopHeader} from '../../components/Section/SectionNew';
import WBReactTable from '../../components/WBReactTable';
import {
  Maybe,
  useStarViewMutation,
  useUnstarViewMutation,
} from '../../generated/graphql';
import {useViewer} from '../../state/viewer/hooks';
import {Graph} from '../../types/graphql';
import {
  DISCUSSION_CATEGORY_ID,
  ReportMetadata,
  useReportMetadata,
} from '../../util/gallery';
import {isPublicRead} from '../../util/projectAccess';
import * as Urls from '../../util/urls';
import {useEntityPageContext, useEntityPageUpdaterContext} from './context';

export interface ReportsTableReport {
  id: string;
  displayName: string;
  description: string;
  user: {
    id: string;
    username: string;
    photoUrl: string;
    admin: boolean;
  };
  project: {
    id: string;
    entityName: string;
    name: string;
    access: Maybe<string>;
  };
  createdAt?: string;
  updatedAt: string;
  starCount: number;
  starred: boolean;
  showcasedAt: Maybe<string>;
  previewUrl: Maybe<string>;
}

interface ReportsTableProps {
  data: Graph<ReportsTableReport> | null;
  pageSize?: number | null;
  onChangePageSize?: (pageSize: number) => void;
  showShowcase?: boolean;
  suppressHeader?: boolean;
  isTeamPage: boolean;
}
const ReportsTable: React.FC<ReportsTableProps> = props => {
  const {data, pageSize, onChangePageSize, showShowcase, isTeamPage} = props;
  const [starView] = useStarViewMutation();
  const [unstarView] = useUnstarViewMutation();

  const {showcasedReportIDs} = useEntityPageContext();
  const {setShowcasedReportIDs} = useEntityPageUpdaterContext();

  const starReport = useCallback(
    (id: string) => starView({variables: {id}}),
    [starView]
  );
  const unstarReport = useCallback(
    (id: string) => unstarView({variables: {id}}),
    [unstarView]
  );
  const showcaseReport = useCallback(
    (r: ReportsTableReport, analyticsLocation: string) => {
      // TODO: replace with makeTrackEvent
      window.analytics?.track('Showcase Report Clicked', {
        location: analyticsLocation,
        reportName: r.displayName,
        reportID: r.id,
        projectName: r.project.name,
      });
      setShowcasedReportIDs(prev =>
        produce(prev, draft => {
          draft.push(r.id);
        })
      );
    },
    [setShowcasedReportIDs]
  );

  const toggleStarred = useCallback(
    (r: ReportsTableReport) =>
      r.starred ? unstarReport(r.id) : starReport(r.id),
    [unstarReport, starReport]
  );

  // The React Table cells are of type react.element so sorting is broken.
  // Extract the relevant values based on the element content. This is pretty
  // brittle so I need to figure out a more consistent way of doing this.
  // Edit this sort function if you add/remove columns from the Reports table.
  // TODO: extract into a util and unit test
  const defaultSortMethod: SortFunction = useCallback((a, b, desc) => {
    // check compare type, and set values accordingly
    let aVal = a;
    let bVal = b;
    // is date value
    if (a.props.date) {
      aVal = a.props.date;
      bVal = b.props.date;
    }
    // is showcase state
    else if (a.props.content) {
      aVal = a.props.content;
      bVal = b.props.content;
    }
    // is star count (children will be an array since it contains the heart
    // symbol and the heart count)
    else if (
      a.props.children &&
      Array.isArray(a.props.children) &&
      a.props.children.length === 2 &&
      a.props.children[0].props.name === 'heart'
    ) {
      aVal = a.props.children[1];
      bVal = b.props.children[1];
    }
    // is link
    else if (a.props.to) {
      aVal = (a.props.children as string).toLowerCase();
      bVal = (b.props.children as string).toLowerCase();
    }

    // perform sort
    if (aVal > bVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }, []);

  // If you edit this, edit defaultSortMethod() above to match.
  const columns = [
    {Header: 'Title', accessor: 'title', width: 300},
    ...(showShowcase
      ? [
          {
            Header: 'Showcase',
            accessor: 'showcase',
            width: 100,
            className: 'showcase-cell',
            headerClassName: 'showcase-cell',
          },
        ]
      : []),
    {Header: '', accessor: 'stars', width: 60, className: 'stars-cell'},
    {Header: 'Project', accessor: 'project', width: 200},
    isTeamPage
      ? {Header: 'Author', accessor: 'author'}
      : {Header: 'Entity', accessor: 'entity'},
    {Header: 'Created On', accessor: 'createdAt'},
    {Header: 'Last Edit', accessor: 'lastEdited'},
  ];

  // Reports without projects are either discussions or blog posts in FC,
  // grab their FC report metadata to form the correct link
  const projectlessReportIDs = useMemo(
    () =>
      (data?.edges ?? [])
        .filter(({node: r}) => r.project == null)
        .map(({node: r}) => r.id),
    [data]
  );
  const {loading: reportsLoading, reportMetadatas} =
    useReportMetadata(projectlessReportIDs);
  const projectlessReportByID = useMemo(() => {
    const reportMap = new Map<string, ReportMetadata>();
    if (!reportsLoading) {
      (reportMetadatas ?? []).forEach(r => {
        reportMap.set(r.id, r);
      });
    }
    return reportMap;
  }, [reportsLoading, reportMetadatas]);

  const {entityName} = useParams<{entityName: string}>();

  const viewer = useViewer();
  const isCurrentViewerProfilePage =
    viewer?.username && entityName === viewer.username;

  const reports = useMemo(
    () =>
      _.compact(
        data?.edges.map(({node: r}) => {
          // if we can't find a projectless report's FC metadata, omit it.
          const projectlessReportMetadata = projectlessReportByID.get(r.id);
          if (r.project == null && projectlessReportMetadata == null) {
            return null;
          }
          const showcaseDisabled =
            r.project != null ? !isPublicRead(r.project) : true;
          const link =
            r.project != null
              ? Urls.reportView({
                  entityName: r.project.entityName,
                  projectName: r.project.name,
                  reportID: r.id,
                  reportName: r.displayName,
                })
              : (projectlessReportMetadata?.tags?.some(
                  t => t.id === DISCUSSION_CATEGORY_ID
                )
                  ? Urls.galleryDiscussionView
                  : Urls.galleryPostEdit)({
                  entityName: projectlessReportMetadata?.entityName ?? '',
                  reportID: r.id,
                  reportName: r.displayName,
                });
          return {
            searchString: r.displayName,
            row: {
              title: <Link to={link}>{r.displayName}</Link>,
              ...(showShowcase
                ? {
                    showcase: !showcasedReportIDs.some(id => id === r.id) ? (
                      <Popup
                        on="hover"
                        position="top center"
                        popperModifiers={{
                          preventOverflow: {enabled: false},
                        }}
                        content={
                          !showcaseDisabled
                            ? 'Showcase this report'
                            : 'Make this project public to showcase this report'
                        }
                        trigger={
                          <span>
                            <LegacyWBIcon
                              name="plus"
                              data-test="showcase-report-button"
                              className={clsx({
                                disabled: showcaseDisabled,
                              })}
                              onClick={() => {
                                if (showcaseDisabled) {
                                  return;
                                }

                                showcaseReport(r, 'reports table');
                              }}
                            />
                          </span>
                        }
                      />
                    ) : (
                      <Popup
                        on="hover"
                        position="top center"
                        popperModifiers={{
                          preventOverflow: {enabled: false},
                        }}
                        content="Featured at the top of your profile"
                        trigger={
                          <span>
                            <LegacyWBIcon
                              name="gallery"
                              data-test="go-to-showcase-button"
                              onClick={scrollToReportShowcase}
                            />
                          </span>
                        }
                      />
                    ),
                  }
                : {}),
              stars: (
                <div
                  style={{alignItems: 'center', display: 'flex', gap: '4px'}}>
                  <Icon
                    onClick={() => toggleStarred(r)}
                    name={r.starred ? 'star-filled' : 'star'}
                    style={{cursor: 'pointer'}}
                  />
                  {r.starCount}
                </div>
              ),
              project:
                r.project != null ? (
                  <LinkNoCrawl
                    to={`/${r.project.entityName}/${r.project.name}`}>
                    {r.project.name}
                  </LinkNoCrawl>
                ) : (
                  <></>
                ),
              entity:
                r.project?.entityName != null ? (
                  <LinkNoCrawl to={`/${r.project.entityName}`}>
                    {r.project.entityName}
                  </LinkNoCrawl>
                ) : (
                  <></>
                ),
              author: (
                <LinkNoCrawl to={`/${r.user.username}`}>
                  {r.user.username}
                </LinkNoCrawl>
              ),
              createdAt: r.createdAt
                ? new Date(r.createdAt).toLocaleDateString('sv-SE')
                : '',
              lastEdited: <TimeAgo date={r.updatedAt} />,
            },
          };
        })
      ),
    [
      data,
      projectlessReportByID,
      showShowcase,
      showcasedReportIDs,
      showcaseReport,
      toggleStarred,
    ]
  );

  const renderHiddenRow = useCallback(
    (r: (typeof reports)[number]['row'], i: number) => (
      <div key={i} style={{display: 'none'}}>
        {r.title}
      </div>
    ),
    // eslint-disable-next-line
    []
  );
  if (reports.length === 0 && !isCurrentViewerProfilePage) {
    return null;
  }

  return (
    <>
      {!props.suppressHeader && <SectionTopHeader>Stars</SectionTopHeader>}
      <WBReactTable
        className="reports-table"
        pageSize={pageSize ?? undefined}
        onChangePageSize={onChangePageSize}
        data={reports}
        columns={columns}
        renderHiddenRow={renderHiddenRow}
        defaultSortMethod={defaultSortMethod}
        noDataComponent={
          reports.length === 0 && isCurrentViewerProfilePage
            ? CustomNoDataComponent
            : undefined
        }
      />
    </>
  );
};

export default ReportsTable;

function scrollToReportShowcase() {
  const el = document.querySelector('#report-showcase');
  if (el != null) {
    el.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'smooth',
    });
  }
}

const CustomNoDataComponent = () => {
  return (
    <Tailwind>
      <div className="my-32 flex flex-col items-center">
        <div className="mb-8 text-2xl font-semibold">No reports yet</div>
        <div className="mb-24 text-center text-lg font-normal leading-[1.575rem] text-moon-650">
          This profile does not have reports yet. They are a great way to
          document your <br /> findings and collaborate on projects. You can get
          started by creating a report.
        </div>
        <Button
          icon="documentation"
          variant="secondary"
          onClick={() =>
            window.open('https://docs.wandb.ai/guides/reports', '_blank')
          }>
          Reports documentation
        </Button>
      </div>
    </Tailwind>
  );
};
